import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useDateRangeStore } from '@/stores/dateRangeStore';
import { salesApi } from '@/services/api';
import type { ChartDataPoint } from '@/types';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444', '#06B6D4', '#EC4899', '#84CC16'];

export function SalesChart() {
  const { t, i18n } = useTranslation();
  const { preset, startDate, endDate } = useDateRangeStore();

  const { data: chartDataRaw, isLoading } = useQuery<{ chartData: ChartDataPoint[] }>({
    queryKey: ['chart-data', preset, startDate.toISOString(), endDate.toISOString()],
    queryFn: () =>
      salesApi.getChartData({
        preset,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        groupBy: 'day',
      }),
    refetchInterval: 60000,
  });

  // Aggregate sales by agent across all dates
  const agentData = useMemo(() => {
    if (!chartDataRaw?.chartData?.length) return [];

    const agentTotals: Record<string, { name: string; totalSales: number; count: number }> = {};

    chartDataRaw.chartData.forEach((point) => {
      point.agents.forEach((agent) => {
        if (!agentTotals[agent.name]) {
          agentTotals[agent.name] = { name: agent.name, totalSales: 0, count: 0 };
        }
        agentTotals[agent.name].totalSales += agent.totalSales;
        agentTotals[agent.name].count += agent.count;
      });
    });

    return Object.values(agentTotals).sort((a, b) => b.totalSales - a.totalSales);
  }, [chartDataRaw]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat(i18n.language === 'he' ? 'he-IL' : 'en-US', {
      style: 'currency',
      currency: 'ILS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatShortCurrency = (value: number) => {
    if (value >= 1000) {
      return `₪${Math.round(value / 1000)}K`;
    }
    return `₪${Math.round(value)}`;
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/4 mb-6"></div>
        <div className="h-64 bg-gray-100 rounded"></div>
      </div>
    );
  }

  // totalSales used for percentage calculations in the Legend formatter
  const _totalSales = agentData.reduce((sum, agent) => sum + agent.totalSales, 0);
  void _totalSales; // Suppress unused warning

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-6">{t('chart.title')}</h2>

      {agentData.length === 0 ? (
        <div className="h-64 flex items-center justify-center text-gray-500">
          {t('chart.noData')}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          {/* Pie Chart */}
          <div className="h-64">
            <h3 className="text-sm font-medium text-gray-600 mb-2 text-center">
              {i18n.language === 'he' ? 'התפלגות מכירות' : 'Sales Distribution'}
            </h3>
            <ResponsiveContainer width="100%" height="90%">
              <PieChart>
                <Pie
                  data={agentData}
                  cx="50%"
                  cy="50%"
                  innerRadius={30}
                  outerRadius={70}
                  paddingAngle={3}
                  dataKey="totalSales"
                  nameKey="name"
                >
                  {agentData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Legend
                  layout="vertical"
                  align="right"
                  verticalAlign="middle"
                  formatter={(value, _entry: any) => {
                    const agent = agentData.find(a => a.name === value);
                    const percent = agent ? ((agent.totalSales / agentData.reduce((s, a) => s + a.totalSales, 0)) * 100).toFixed(0) : 0;
                    return `${value} (${percent}%)`;
                  }}
                />
                <Tooltip
                  formatter={(value: number) => [formatCurrency(value), '']}
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Bar Chart */}
          <div className="h-64">
            <h3 className="text-sm font-medium text-gray-600 mb-2 text-center">
              {i18n.language === 'he' ? 'מכירות לפי סוכן' : 'Sales by Agent'}
            </h3>
            <ResponsiveContainer width="100%" height="90%">
              <BarChart data={agentData} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis
                  type="number"
                  tick={{ fill: '#6B7280', fontSize: 11 }}
                  tickLine={{ stroke: '#E5E7EB' }}
                  axisLine={{ stroke: '#E5E7EB' }}
                  tickFormatter={formatShortCurrency}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                  tickLine={{ stroke: '#E5E7EB' }}
                  axisLine={{ stroke: '#E5E7EB' }}
                  width={70}
                />
                <Tooltip
                  formatter={(value: number, name: string) => [formatCurrency(value), name]}
                  labelFormatter={(label) => label}
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    direction: i18n.language === 'he' ? 'rtl' : 'ltr',
                  }}
                  wrapperStyle={{ zIndex: 1000 }}
                />
                <Bar dataKey="totalSales" radius={[0, 4, 4, 0]} fill="#3B82F6">
                  {agentData.map((_, index) => (
                    <Cell key={`bar-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
